import type { BattleReport, BattleResult } from "../../../shared/types/BattleEvent";
import type { CommanderProfile } from "../../../shared/types/CommanderProfile";
import type { AchievementId, BattleLearningSnapshot, PlayerMemory, RewardSummary, RivalryStage, TacticalPatternKey, TacticalPatternMemory, VeteranUnitMemory } from "../../../shared/types/PlayerMemory";
import type { Language } from "../i18n";
import { isBattleMode, modeRewardMultiplier } from "../game/battleMode";
import { type UpgradeId, upgradeCost } from "../game/shop";
import { dailyChallengeFromSeed, dailyScore, dateKeyOf, nextDailyRecord } from "../game/dailyChallenge";
import { DEFAULT_SKIN_ID, SKINS, skinColor, type SkinId } from "../game/skins";

export const STORAGE_KEYS = {
  playerId: "DSAI_PLAYER_ID",
  commanderProfile: "DSAI_COMMANDER_PROFILE",
  playerMemory: "DSAI_PLAYER_MEMORY",
  settings: "DSAI_SETTINGS",
  battleHistory: "DSAI_BATTLE_HISTORY",
  tutorialDone: "DSAI_TUTORIAL_DONE"
} as const;

export type BetaFeatureKey = "cameraZoom" | "spriteAnimations" | "contactCombat" | "cloudBackend";

export type GameSettings = {
  beta: Record<BetaFeatureKey, boolean>;
  cameraZoom: number;
  highContrastText: boolean;
  uiScale: number;
  language: Language;
  muted: boolean;
};

export type BattleHistoryEntry = {
  result: BattleResult;
  report?: BattleReport;
  savedAt: string;
};

export const DEFAULT_SETTINGS: GameSettings = {
  beta: {
    cameraZoom: true,
    spriteAnimations: true,
    contactCombat: true,
    cloudBackend: false
  },
  cameraZoom: 1,
  highContrastText: false,
  uiScale: 1,
  language: "ko",
  muted: false
};

function defaultPlayerMemory(playerId: string): PlayerMemory {
  return {
    playerId,
    battleCount: 0,
    wins: 0,
    losses: 0,
    averageSurvivalTime: 0,
    favoriteTowers: [],
    favoriteUnits: [],
    weakLanes: [],
    strongLanes: [],
    commonPatterns: [],
    lostToStrategies: [],
    wonAgainstStrategies: [],
    knownPatterns: [],
    patternConfidence: {},
    preferredOpeningCounters: [],
    veterans: [],
    commanderGrudgeLevel: 0,
    rivalryScore: 0,
    rivalryStage: "unknown",
    notableHistory: [],
    recentLearnings: [],
    commanderLevel: 1,
    commanderXp: 0,
    warFunds: 0,
    lifetimeWarFunds: 0,
    bestEndlessTime: 0,
    upgrades: {},
    achievements: {},
    ownedSkins: [DEFAULT_SKIN_ID],
    equippedSkin: DEFAULT_SKIN_ID,
    summaryForPrompt: "No battles recorded yet. The player is still unknown.",
    updatedAt: new Date().toISOString()
  };
}

function normalizeMemory(playerId: string, memory?: PlayerMemory): PlayerMemory {
  const fallback = defaultPlayerMemory(playerId);
  return {
    ...fallback,
    ...(memory ?? {}),
    playerId: memory?.playerId ?? playerId,
    favoriteTowers: memory?.favoriteTowers ?? fallback.favoriteTowers,
    favoriteUnits: memory?.favoriteUnits ?? fallback.favoriteUnits,
    weakLanes: memory?.weakLanes ?? fallback.weakLanes,
    strongLanes: memory?.strongLanes ?? fallback.strongLanes,
    commonPatterns: memory?.commonPatterns ?? fallback.commonPatterns,
    lostToStrategies: memory?.lostToStrategies ?? fallback.lostToStrategies,
    wonAgainstStrategies: memory?.wonAgainstStrategies ?? fallback.wonAgainstStrategies,
    knownPatterns: memory?.knownPatterns ?? fallback.knownPatterns,
    patternConfidence: memory?.patternConfidence ?? fallback.patternConfidence,
    preferredOpeningCounters: memory?.preferredOpeningCounters ?? fallback.preferredOpeningCounters,
    veterans: memory?.veterans ?? fallback.veterans,
    rivalryScore: memory?.rivalryScore ?? fallback.rivalryScore,
    rivalryStage: memory?.rivalryStage ?? fallback.rivalryStage,
    notableHistory: memory?.notableHistory ?? fallback.notableHistory,
    recentLearnings: memory?.recentLearnings ?? fallback.recentLearnings,
    commanderLevel: memory?.commanderLevel ?? fallback.commanderLevel,
    commanderXp: memory?.commanderXp ?? fallback.commanderXp,
    warFunds: memory?.warFunds ?? fallback.warFunds,
    lifetimeWarFunds: memory?.lifetimeWarFunds ?? fallback.lifetimeWarFunds,
    bestEndlessTime: memory?.bestEndlessTime ?? fallback.bestEndlessTime,
    upgrades: memory?.upgrades ?? fallback.upgrades,
    achievements: memory?.achievements ?? fallback.achievements,
    dailyChallenge: memory?.dailyChallenge ?? fallback.dailyChallenge,
    // The default banner is always owned so the player can never strand themselves without one.
    ownedSkins: Array.from(new Set([DEFAULT_SKIN_ID, ...(memory?.ownedSkins ?? [])])),
    equippedSkin: memory?.equippedSkin ?? fallback.equippedSkin
  };
}

function readJson<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch {
    return undefined;
  }
}

function writeJson(key: string, value: unknown) {
  localStorage.setItem(key, JSON.stringify(value));
}

export function getOrCreatePlayerId() {
  const existing = localStorage.getItem(STORAGE_KEYS.playerId);
  if (existing) return existing;
  // crypto.randomUUID is undefined outside secure contexts (plain HTTP / some webviews);
  // fall back to a timestamp+random id so early boot never hard-crashes.
  const unique = crypto?.randomUUID?.().slice(0, 8) ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  const playerId = `p_${unique}`;
  localStorage.setItem(STORAGE_KEYS.playerId, playerId);
  return playerId;
}

export function loadCommanderProfile() {
  return readJson<CommanderProfile>(STORAGE_KEYS.commanderProfile);
}

export function saveCommanderProfile(profile: CommanderProfile) {
  writeJson(STORAGE_KEYS.commanderProfile, profile);
}

export function loadPlayerMemory(playerId: string): PlayerMemory {
  return normalizeMemory(playerId, readJson<PlayerMemory>(STORAGE_KEYS.playerMemory));
}

export function savePlayerMemory(memory: PlayerMemory) {
  writeJson(STORAGE_KEYS.playerMemory, memory);
}

/**
 * Spend War Funds to buy the next level of a permanent upgrade. Returns the updated memory
 * on success, or null if maxed / not enough funds. Persists on success.
 */
export function purchaseUpgrade(playerId: string, id: UpgradeId): PlayerMemory | null {
  const memory = loadPlayerMemory(playerId);
  const level = memory.upgrades[id] ?? 0;
  const cost = upgradeCost(id, level);
  if (cost === null) return null; // maxed
  if (memory.warFunds < cost) return null; // can't afford
  memory.warFunds -= cost;
  memory.upgrades = { ...memory.upgrades, [id]: level + 1 };
  memory.updatedAt = new Date().toISOString();
  savePlayerMemory(memory);
  return memory;
}

/**
 * Spend War Funds to buy a cosmetic commander banner. Auto-equips on purchase. Returns the
 * updated memory on success, or null if already owned / not enough funds. Persists on success.
 */
export function purchaseSkin(playerId: string, id: SkinId): PlayerMemory | null {
  const memory = loadPlayerMemory(playerId);
  const owned = memory.ownedSkins ?? [DEFAULT_SKIN_ID];
  if (owned.includes(id)) return null; // already owned
  const cost = SKINS[id]?.cost ?? 0;
  if (memory.warFunds < cost) return null; // can't afford
  memory.warFunds -= cost;
  memory.ownedSkins = [...owned, id];
  memory.equippedSkin = id;
  memory.updatedAt = new Date().toISOString();
  savePlayerMemory(memory);
  return memory;
}

/** Equip an owned commander banner. Returns the updated memory, or null if not owned. */
export function equipSkin(playerId: string, id: SkinId): PlayerMemory | null {
  const memory = loadPlayerMemory(playerId);
  const owned = memory.ownedSkins ?? [DEFAULT_SKIN_ID];
  if (!owned.includes(id)) return null;
  memory.equippedSkin = id;
  memory.updatedAt = new Date().toISOString();
  savePlayerMemory(memory);
  return memory;
}

/** Phaser fill color of the player's currently equipped banner (default-safe). */
export function equippedSkinColor(memory: PlayerMemory): number {
  return skinColor(memory.equippedSkin);
}

export function loadSettings(): GameSettings {
  const saved = readJson<Partial<GameSettings>>(STORAGE_KEYS.settings);
  return {
    ...DEFAULT_SETTINGS,
    ...saved,
    beta: {
      ...DEFAULT_SETTINGS.beta,
      ...(saved?.beta ?? {})
    }
  };
}

export function saveSettings(settings: GameSettings) {
  writeJson(STORAGE_KEYS.settings, settings);
}

export function isBetaFeatureEnabled(feature: BetaFeatureKey) {
  return loadSettings().beta[feature];
}

export function toggleBetaFeature(feature: BetaFeatureKey) {
  const settings = loadSettings();
  settings.beta[feature] = !settings.beta[feature];
  saveSettings(settings);
  return settings;
}

export function setLanguage(language: Language) {
  const settings = loadSettings();
  settings.language = language;
  saveSettings(settings);
  return settings;
}

export function toggleHighContrastText() {
  const settings = loadSettings();
  settings.highContrastText = !settings.highContrastText;
  saveSettings(settings);
  return settings;
}

export function toggleMuted() {
  const settings = loadSettings();
  settings.muted = !settings.muted;
  saveSettings(settings);
  return settings;
}

export function cycleUiScale() {
  const steps = [0.9, 1, 1.15, 1.3];
  const settings = loadSettings();
  const current = steps.findIndex((step) => Math.abs(step - (settings.uiScale ?? 1)) < 0.01);
  settings.uiScale = steps[(current + 1) % steps.length] ?? 1;
  saveSettings(settings);
  return settings;
}

export function hasSeenTutorial() {
  return localStorage.getItem(STORAGE_KEYS.tutorialDone) === "true";
}

export function setTutorialSeen() {
  localStorage.setItem(STORAGE_KEYS.tutorialDone, "true");
}

export function appendBattleHistory(result: BattleResult, report?: BattleReport) {
  const history = readJson<BattleHistoryEntry[]>(STORAGE_KEYS.battleHistory) ?? [];
  history.push({ result, report, savedAt: new Date().toISOString() });
  writeJson(STORAGE_KEYS.battleHistory, history.slice(-20));
}

export function loadBattleHistory() {
  return readJson<BattleHistoryEntry[]>(STORAGE_KEYS.battleHistory) ?? [];
}

function rankedEventValues(result: BattleResult, eventTypes: string[], payloadKey: string) {
  const counts = new Map<string, number>();
  for (const event of result.events) {
    if (!eventTypes.includes(event.type)) continue;
    const value = event.payload[payloadKey];
    if (typeof value !== "string" || !value) continue;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([value]) => value);
}

function rankedWeakLanes(result: BattleResult) {
  const counts = new Map<number, number>();
  for (const event of result.events) {
    if (!event.lane || (event.type !== "LANE_BREACHED" && event.type !== "CASTLE_DAMAGED" && event.type !== "PLAYER_UNIT_KILLED")) continue;
    if (event.type === "CASTLE_DAMAGED" && event.payload.owner !== "player") continue;
    counts.set(event.lane, (counts.get(event.lane) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([lane]) => lane);
}

function rankedStrongLanes(result: BattleResult) {
  const counts = new Map<number, number>();
  for (const event of result.events) {
    if (!event.lane || event.type !== "ENEMY_KILLED") continue;
    counts.set(event.lane, (counts.get(event.lane) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([lane]) => lane);
}

function mergeRecent<T>(existing: T[], next: T[], limit: number) {
  return [...next, ...existing.filter((item) => !next.includes(item))].slice(0, limit);
}

type PatternDetection = {
  key: TacticalPatternKey;
  confidenceDelta: number;
  preferredLane?: number;
};

function topLane(counts: Map<number, number>) {
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
}

function countByLane(result: BattleResult, eventType: string) {
  const counts = new Map<number, number>();
  for (const event of result.events) {
    if (event.type !== eventType || !event.lane) continue;
    counts.set(event.lane, (counts.get(event.lane) ?? 0) + 1);
  }
  return counts;
}

function countByLaneForTypes(result: BattleResult, eventTypes: string[]) {
  const counts = new Map<number, number>();
  for (const event of result.events) {
    if (!eventTypes.includes(event.type) || !event.lane) continue;
    counts.set(event.lane, (counts.get(event.lane) ?? 0) + 1);
  }
  return counts;
}

function hasBurst(events: BattleResult["events"], minimumCount: number, windowSec: number) {
  const times = events.map((event) => event.time).sort((a, b) => a - b);
  for (let index = 0; index < times.length; index += 1) {
    const end = times[index]! + windowSec;
    if (times.filter((time) => time >= times[index]! && time <= end).length >= minimumCount) return true;
  }
  return false;
}

function detectBattlePatterns(result: BattleResult, weakLanes: number[]): PatternDetection[] {
  const detections: PatternDetection[] = [];
  const summonEvents = result.events.filter((event) => event.type === "UNIT_SUMMONED");
  const summonByLane = countByLane(result, "UNIT_SUMMONED");
  const totalSummons = summonEvents.length;
  const topSummon = topLane(summonByLane);
  if (topSummon && totalSummons >= 4 && topSummon[0] === 3 && topSummon[1] / totalSummons >= 0.45) {
    detections.push({ key: "center_commit", confidenceDelta: 0.55, preferredLane: 3 });
  }
  if (topSummon && totalSummons >= 6 && topSummon[1] / totalSummons >= 0.6) {
    detections.push({ key: "single_lane_all_in", confidenceDelta: 0.6, preferredLane: topSummon[0] });
  }
  if (summonEvents.length >= 7 && summonEvents[0] && summonEvents[0].time >= 38 && hasBurst(summonEvents, 5, 18)) {
    detections.push({ key: "gold_bank_rush", confidenceDelta: 0.55, preferredLane: topSummon?.[0] });
  }

  const towerEvents = result.events.filter((event) => event.type === "TOWER_PLACED");
  const towerByLane = countByLaneForTypes(result, ["TOWER_PLACED", "TOWER_UPGRADED"]);
  if (towerEvents.length >= 5) {
    detections.push({ key: "tower_turtle", confidenceDelta: 0.45 });
  }
  if (towerEvents.filter((event) => event.payload.towerId === "mortar_emplacement").length >= 2) {
    detections.push({ key: "mortar_stack", confidenceDelta: 0.65 });
  }
  const towerAnchor = topLane(countByLaneForTypes(result, ["AI_TOWER_TARGETED"]));
  const builtAnchor = topLane(towerByLane);
  if (towerAnchor && towerAnchor[1] >= 1) {
    detections.push({ key: "priority_tower_anchor", confidenceDelta: 0.55, preferredLane: towerAnchor[0] });
  } else if (builtAnchor && builtAnchor[1] >= 3) {
    detections.push({ key: "priority_tower_anchor", confidenceDelta: 0.4, preferredLane: builtAnchor[0] });
  }

  const counterEvent = result.events.find((event) => event.type === "COUNTER_ATTACK_STARTED");
  if (counterEvent && counterEvent.time >= Math.max(75, result.battleTime * 0.58)) {
    detections.push({ key: "late_counter", confidenceDelta: 0.45 });
  }
  if (result.events.filter((event) => event.type === "UNIT_MOVE_COMMAND" || event.type === "UNIT_LANE_CHANGED").length >= 5) {
    detections.push({ key: "wide_rotation", confidenceDelta: 0.35 });
  }
  const summonByWeakLane = countByLaneForTypes(result, ["UNIT_SUMMONED", "UNIT_MOVE_COMMAND"]);
  const defenseByWeakLane = countByLaneForTypes(result, ["TOWER_PLACED", "TOWER_UPGRADED"]);
  const ignoredLane = weakLanes.find((lane) => (summonByWeakLane.get(lane) ?? 0) <= 1 && (defenseByWeakLane.get(lane) ?? 0) <= 1);
  if (ignoredLane) {
    detections.push({ key: "ignored_lane", confidenceDelta: 0.5, preferredLane: ignoredLane });
  }
  if (weakLanes.some((lane) => lane <= 2)) {
    detections.push({ key: "weak_top", confidenceDelta: 0.35, preferredLane: weakLanes.find((lane) => lane <= 2) });
  }
  if (weakLanes.some((lane) => lane >= 4)) {
    detections.push({ key: "weak_bottom", confidenceDelta: 0.35, preferredLane: weakLanes.find((lane) => lane >= 4) });
  }
  return detections;
}

function counterForPattern(pattern: TacticalPatternMemory) {
  const lane = pattern.preferredLane ?? 3;
  switch (pattern.key) {
    case "center_commit":
    case "single_lane_all_in":
      return `reinforce_lane_${lane}`;
    case "gold_bank_rush":
      return `early_counter_reserve_lane_${lane}`;
    case "tower_turtle":
      return "siege_and_flanker_pressure";
    case "mortar_stack":
      return "mortar_hunter_pressure";
    case "priority_tower_anchor":
      return `early_siege_lane_${lane}`;
    case "ignored_lane":
      return `punish_open_lane_${lane}`;
    case "late_counter":
      return "bait_counterattack_before_commit";
    case "weak_top":
      return "pressure_top_lanes";
    case "weak_bottom":
      return "pressure_bottom_lanes";
    case "wide_rotation":
      return "guard_open_crossings";
    default:
      return `probe_lane_${lane}`;
  }
}

function updateTacticalPatterns(existing: TacticalPatternMemory[], detections: PatternDetection[], result: BattleResult) {
  const now = new Date().toISOString();
  const byKey = new Map<TacticalPatternKey, TacticalPatternMemory>();
  for (const pattern of existing) {
    byKey.set(pattern.key, {
      ...pattern,
      confidence: Math.max(0, Number((pattern.confidence * 0.92).toFixed(2)))
    });
  }
  for (const detection of detections) {
    const current = byKey.get(detection.key);
    byKey.set(detection.key, {
      key: detection.key,
      confidence: Math.min(3, Number(((current?.confidence ?? 0) + detection.confidenceDelta).toFixed(2))),
      seenCount: (current?.seenCount ?? 0) + 1,
      lastSeenAt: now,
      lastBattleResult: result.result,
      preferredLane: detection.preferredLane ?? current?.preferredLane
    });
  }
  return [...byKey.values()]
    .filter((pattern) => pattern.confidence >= 0.25)
    .sort((a, b) => b.confidence - a.confidence || b.seenCount - a.seenCount)
    .slice(0, 10);
}

function memorySummary(result: BattleResult, report: BattleReport | undefined, weakLanes: number[], strongLanes: number[], patterns: TacticalPatternMemory[], veterans: VeteranUnitMemory[], memory: PlayerMemory) {
  const outcome = result.result === "win" ? "won" : "lost";
  const strategy = result.strategyFaced ?? "unknown pressure";
  const weak = weakLanes.length ? ` weak lanes ${weakLanes.join(",")}` : "";
  const strong = strongLanes.length ? ` strong lanes ${strongLanes.join(",")}` : "";
  const rivalry = ` Rivalry: ${memory.rivalryStage} score ${memory.rivalryScore}${memory.nickname ? `, nickname "${memory.nickname}" (${memory.nicknameReason ?? "observed pattern"})` : ""}.`;
  const patternSummary = patterns.length
    ? ` Known patterns: ${patterns.slice(0, 4).map((pattern) => `${pattern.key}${pattern.preferredLane ? ` lane ${pattern.preferredLane}` : ""} confidence ${pattern.confidence}`).join("; ")}.`
    : "";
  const veteranSummary = veterans.length
    ? ` Veterans: ${veterans.slice(0, 3).map((veteran) => `${veteran.nickname} (${veteran.unitId}, ${veteran.kills} kills)`).join("; ")}.`
    : "";
  const learningSummary = memory.recentLearnings?.[0]
    ? ` Latest learning: ${memory.recentLearnings[0].openingPattern}, lane ${memory.recentLearnings[0].mainThreatLane ?? "?"}, bias ${memory.recentLearnings[0].nextThreatBias}.`
    : "";
  const reportSummary = report?.memoryUpdateSummary ? `${report.memoryUpdateSummary} ` : "";
  return `${reportSummary}Player ${outcome} after ${result.battleTime}s against ${strategy}.${weak}.${strong}.${rivalry}${patternSummary}${learningSummary}${veteranSummary}`.trim();
}

function classifyBattleLearning(result: BattleResult, detections: PatternDetection[], weakLanes: number[]): BattleLearningSnapshot {
  const laneAnalysis = result.laneAnalysis ?? [];
  const mainLane = [...laneAnalysis].sort((a, b) => b.playerUnitsSummoned - a.playerUnitsSummoned || b.playerDamageToAiCastle - a.playerDamageToAiCastle)[0];
  const ignoredLane = [...laneAnalysis]
    .filter((lane) => lane.playerUnitsSummoned <= 1 && lane.moveCommands <= 1)
    .sort((a, b) => b.aiDamageToPlayerCastle - a.aiDamageToPlayerCastle || b.laneBreaches - a.laneBreaches)[0]?.lane;
  const counterEvent = result.events.find((event) => event.type === "COUNTER_ATTACK_STARTED");
  const counterTiming =
    !counterEvent ? "none" : counterEvent.time < result.battleTime * 0.35 ? "early" : counterEvent.time < result.battleTime * 0.65 ? "mid" : "late";
  const openingPattern = detections[0]?.key ?? (mainLane && mainLane.playerUnitsSummoned >= 5 ? `lane_${mainLane.lane}_commit` : "balanced_opening");
  const collapsedLane = laneAnalysis.find((lane) => lane.verdict === "collapsed") ?? laneAnalysis.find((lane) => lane.aiDamageToPlayerCastle > 0);
  const breakthroughLane = laneAnalysis.find((lane) => lane.verdict === "breakthrough") ?? laneAnalysis.find((lane) => lane.playerDamageToAiCastle > 0);
  const nextThreatBias =
    ignoredLane !== undefined
      ? `punish_ignored_lane_${ignoredLane}`
      : detections[0]?.preferredLane
      ? `counter_pattern_${detections[0].key}_lane_${detections[0].preferredLane}`
      : weakLanes[0]
      ? `pressure_weak_lane_${weakLanes[0]}`
      : "probe_lane_balance";
  return {
    at: new Date().toISOString(),
    result: result.result,
    openingPattern,
    mainThreatLane: mainLane?.lane,
    ignoredLane,
    counterTiming,
    lostBecause: result.result === "loss" ? (collapsedLane ? `lane_${collapsedLane.lane}_${collapsedLane.verdict}` : "castle_pressure") : undefined,
    wonBecause: result.result === "win" ? (breakthroughLane ? `lane_${breakthroughLane.lane}_${breakthroughLane.verdict}` : "castle_held") : undefined,
    nextThreatBias
  };
}

function mergeVeterans(existing: VeteranUnitMemory[], result: BattleResult) {
  const byId = new Map(existing.map((veteran) => [veteran.veteranId, veteran]));
  const recorded = result.events.filter((event) => event.type === "VETERAN_RECORDED");
  for (const event of recorded) {
    const veteranId = String(event.payload.veteranId ?? "");
    const unitId = String(event.payload.unitId ?? "");
    if (!veteranId || !unitId) continue;
    const current = byId.get(veteranId);
    const kills = Number(event.payload.kills ?? current?.kills ?? 0);
    const survivedBattles = Number(event.payload.survivedBattles ?? current?.survivedBattles ?? 0);
    const lane = Number(event.lane ?? event.payload.preferredLane);
    byId.set(veteranId, {
      veteranId,
      unitId,
      nickname: String(event.payload.nickname ?? current?.nickname ?? "Veteran"),
      bonus: current?.bonus ?? { hpRatio: 0.12, damageRatio: 0.08, speedRatio: 0.04 },
      survivedBattles: Math.max(current?.survivedBattles ?? 0, Number.isFinite(survivedBattles) ? survivedBattles : 1),
      kills: Math.max(current?.kills ?? 0, Number.isFinite(kills) ? kills : 0),
      preferredLane: Number.isFinite(lane) ? lane : current?.preferredLane,
      lastSeenAt: new Date().toISOString()
    });
  }
  return [...byId.values()]
    .sort((a, b) => b.survivedBattles - a.survivedBattles || b.kills - a.kills)
    .slice(0, 6);
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function rivalryStage(score: number, wins: number, losses: number): RivalryStage {
  if (score < 4) return "unknown";
  if (score < 10) return "annoyed";
  if (score < 22) return "rival";
  if (wins >= losses && score >= 18) return "respectful_enemy";
  return "obsessed";
}

function updateRivalry(normalizedMemory: PlayerMemory, result: BattleResult, detections: PatternDetection[], wins: number, losses: number) {
  const mercyCount = result.events.filter((event) => event.type === "AI_MERCY_GRANTED").length;
  const comeback = result.result === "win" && result.playerCastleHp <= 180;
  const closeLoss = result.result === "loss" && result.aiCastleHp <= 180;
  const scoreDelta = (result.result === "win" ? 4 : 2) + Math.min(2, detections.length) + (comeback ? 3 : 0) + (closeLoss ? 1 : 0) - mercyCount;
  const rivalryScore = clamp(Math.round((normalizedMemory.rivalryScore ?? 0) * 0.96 + scoreDelta), 0, 100);
  const stage = rivalryStage(rivalryScore, wins, losses);
  const note = result.result === "win"
    ? comeback ? "역전승을 기록해 지휘관의 경계가 크게 올랐다." : "승리 패턴이 라이벌 기억에 저장되었다."
    : closeLoss ? "패배했지만 적 요새를 거의 무너뜨렸다." : "패배 전선과 대응 지연이 기록되었다.";
  return {
    rivalryScore,
    rivalryStage: stage,
    notableHistory: [
      { at: new Date().toISOString(), result: result.result, stage, note },
      ...(normalizedMemory.notableHistory ?? [])
    ].slice(0, 8)
  };
}

function nicknameCandidate(patterns: TacticalPatternMemory[], result: BattleResult) {
  if ((result.mercySummary?.pleaCount ?? 0) >= 1) {
    return { nickname: "무릎 꿇은 군주", reason: "자비를 구해 전투를 연장함" };
  }
  const strongest = [...patterns].sort((a, b) => b.confidence - a.confidence || b.seenCount - a.seenCount)[0];
  if (!strongest || strongest.confidence < 0.95) return undefined;
  switch (strongest.key) {
    case "center_commit":
      return { nickname: "중앙 파괴자", reason: "중앙 전선 집중 공격을 반복함" };
    case "single_lane_all_in":
      return { nickname: "한길 군주", reason: `${strongest.preferredLane ?? 3}번 전선에 병력을 집중함` };
    case "tower_turtle":
      return { nickname: "성벽 장인", reason: "타워와 방어선을 두껍게 쌓음" };
    case "mortar_stack":
      return { nickname: "박격포 광신도", reason: "박격포 진지를 반복적으로 중첩함" };
    case "gold_bank_rush":
      return { nickname: "금고 습격자", reason: "골드를 모아 한 번에 밀어붙임" };
    case "late_counter":
      return { nickname: "늦은 나팔수", reason: "후반 반격 타이밍을 자주 노림" };
    case "wide_rotation":
      return { nickname: "전선 유랑자", reason: "유닛을 여러 전선으로 자주 재배치함" };
    case "ignored_lane":
      return { nickname: "빈문 방치자", reason: "특정 전선을 자주 비워 둠" };
    case "priority_tower_anchor":
      return { nickname: "거점 설계자", reason: "핵심 타워 거점에 방어를 고정함" };
    default:
      if (result.result === "win") return { nickname: "성문 파괴자", reason: "요새 돌파 승리를 기록함" };
      return undefined;
  }
}

/**
 * Total XP required to be AT a given commander level. Gentle quadratic so early levels come
 * fast (dopamine) and later ones slow down. Level 1 starts at 0 XP.
 */
export function xpForLevel(level: number): number {
  const l = Math.max(1, Math.floor(level));
  return Math.round(60 * (l - 1) + 20 * (l - 1) * (l - 1));
}

/** Commander level reachable with a given total XP (inverse of xpForLevel, by scan). */
export function levelForXp(totalXp: number): number {
  let level = 1;
  while (xpForLevel(level + 1) <= totalXp) level += 1;
  return level;
}

type RivalryOutcome = { rivalryStage: RivalryStage };

/**
 * Pure per-battle reward math. XP/funds are tied to rivalry-flavored events (not just wins)
 * so progression reads as "the AI takes you more seriously". Returns the new running totals
 * plus a player-facing summary. No persistence — caller folds this into PlayerMemory.
 */
export function computeBattleRewards(
  memory: PlayerMemory,
  result: BattleResult,
  detections: PatternDetection[],
  rivalry: RivalryOutcome,
  veterans: VeteranUnitMemory[]
): { commanderLevel: number; commanderXp: number; warFunds: number; lifetimeWarFunds: number; summary: RewardSummary } {
  const reasons: string[] = [];
  let xp = 0;
  let funds = 0;

  // Base participation.
  xp += 20;
  funds += 20;
  reasons.push("battle");

  const win = result.result === "win";
  if (win) {
    xp += 60;
    funds += 60;
    reasons.push("win");
  }

  // Comeback win (low castle HP) / honorable close loss (nearly cracked the AI fortress).
  if (win && result.playerCastleHp <= 180) {
    xp += 40;
    funds += 30;
    reasons.push("comeback");
  } else if (!win && result.aiCastleHp <= 180) {
    xp += 15;
    funds += 10;
    reasons.push("closeLoss");
  }

  // Rivalry stage escalation — the signature hook.
  const stageRank: Record<RivalryStage, number> = { unknown: 0, annoyed: 1, rival: 2, obsessed: 3, respectful_enemy: 3 };
  const prevStage = memory.rivalryStage ?? "unknown";
  if ((stageRank[rivalry.rivalryStage] ?? 0) > (stageRank[prevStage] ?? 0)) {
    xp += 50;
    funds += 40;
    reasons.push("rivalryUp");
  }

  // First time defeating a given AI strategy.
  const strategy = result.strategyFaced;
  if (win && strategy && !(memory.wonAgainstStrategies ?? []).includes(strategy)) {
    xp += 25;
    funds += 20;
    reasons.push("newStrategyBeaten");
  }

  // Veterans that survived this battle (grew their survivedBattles) — loyalty payoff.
  const survivingVeterans = veterans.filter((v) => v.survivedBattles > 0).length;
  if (survivingVeterans > 0) {
    const bonus = Math.min(30, survivingVeterans * 6);
    xp += bonus;
    funds += bonus;
    reasons.push("veterans");
  }

  // Pattern variety (the AI is learning more about you).
  funds += Math.min(15, detections.length * 5);

  // Harder modes pay out more War Funds for the same fight.
  const multiplier = modeRewardMultiplier(isBattleMode(result.mode) ? result.mode : "normal");
  if (multiplier > 1) {
    funds = Math.round(funds * multiplier);
    reasons.push("mode");
  }

  const commanderXp = Math.max(0, Math.round((memory.commanderXp ?? 0) + xp));
  const prevLevel = Math.max(1, memory.commanderLevel ?? 1);
  const newLevel = levelForXp(commanderXp);
  const leveledUp = newLevel > prevLevel;
  const warFunds = Math.max(0, Math.round((memory.warFunds ?? 0) + funds));
  const lifetimeWarFunds = Math.max(0, Math.round((memory.lifetimeWarFunds ?? 0) + funds));

  return {
    commanderLevel: newLevel,
    commanderXp,
    warFunds,
    lifetimeWarFunds,
    summary: { xp, funds, leveledUp, newLevel, reasons, unlockedAchievements: [] }
  };
}

/** All achievement ids in display order, so the career dashboard can iterate them. */
export const ACHIEVEMENT_IDS: AchievementId[] = [
  "first_blood",
  "first_win",
  "comeback_king",
  "rival_reached",
  "obsession",
  "respected",
  "veteran_corps",
  "strategist",
  "level_5",
  "level_10",
  "persistent",
  "nemesis_slayer",
  "daily_clear",
  "streak_7",
  "endless_survivor",
  "collector",
  "war_economy",
  "level_20"
];

/**
 * Pure achievement evaluation. Given the running totals (already updated for this battle),
 * returns the new achievements map plus the ids unlocked THIS battle (for a toast). Most
 * milestones are tied to the rivalry hook so they reinforce the game's signature loop.
 */
export function evaluateAchievements(
  previous: PlayerMemory["achievements"],
  context: {
    result: BattleResult;
    wins: number;
    battleCount: number;
    rivalryStage: RivalryStage;
    commanderLevel: number;
    veterans: VeteranUnitMemory[];
    wonAgainstStrategies: string[];
    rewardReasons: string[];
    dailyStreak: number;
    dailyCompleted: boolean;
    bestEndlessTime: number;
    ownedSkinsCount: number;
    lifetimeWarFunds: number;
  }
): { achievements: PlayerMemory["achievements"]; unlocked: AchievementId[] } {
  const achievements = { ...(previous ?? {}) };
  const unlocked: AchievementId[] = [];
  const now = new Date().toISOString();
  const grant = (id: AchievementId, condition: boolean) => {
    if (condition && !achievements[id]) {
      achievements[id] = { unlockedAt: now };
      unlocked.push(id);
    }
  };

  grant("first_blood", context.battleCount >= 1);
  grant("first_win", context.wins >= 1);
  grant("comeback_king", context.result.result === "win" && context.rewardReasons.includes("comeback"));
  grant("rival_reached", context.rivalryStage === "rival" || context.rivalryStage === "obsessed" || context.rivalryStage === "respectful_enemy");
  grant("obsession", context.rivalryStage === "obsessed");
  grant("respected", context.rivalryStage === "respectful_enemy");
  grant("veteran_corps", context.veterans.filter((v) => v.survivedBattles >= 1).length >= 3);
  // Beaten at least 4 distinct AI strategies over time.
  grant("strategist", new Set(context.wonAgainstStrategies).size >= 4);
  grant("level_5", context.commanderLevel >= 5);
  grant("level_10", context.commanderLevel >= 10);
  grant("level_20", context.commanderLevel >= 20);
  grant("persistent", context.battleCount >= 20);
  // Nemesis Slayer: beat the AI on Hard once the rivalry has truly escalated.
  grant("nemesis_slayer", context.result.result === "win" && context.result.mode === "hard" && (context.rivalryStage === "obsessed" || context.rivalryStage === "respectful_enemy"));
  grant("daily_clear", context.dailyCompleted);
  grant("streak_7", context.dailyStreak >= 7);
  grant("endless_survivor", context.bestEndlessTime >= 180);
  grant("collector", context.ownedSkinsCount >= 3);
  grant("war_economy", context.lifetimeWarFunds >= 2500);

  return { achievements, unlocked };
}

export function updateMemoryFromReport(memory: PlayerMemory, result: BattleResult, report?: BattleReport): PlayerMemory {
  const normalizedMemory = normalizeMemory(memory.playerId, memory);
  const battleCount = normalizedMemory.battleCount + 1;
  const wins = normalizedMemory.wins + (result.result === "win" ? 1 : 0);
  const losses = normalizedMemory.losses + (result.result === "loss" ? 1 : 0);
  const averageSurvivalTime =
    normalizedMemory.averageSurvivalTime === 0 ? result.battleTime : Math.round((normalizedMemory.averageSurvivalTime * normalizedMemory.battleCount + result.battleTime) / battleCount);
  const favoriteTowers = rankedEventValues(result, ["TOWER_PLACED", "TOWER_UPGRADED"], "towerId");
  const favoriteUnits = rankedEventValues(result, ["UNIT_SUMMONED", "UNIT_UPGRADED"], "unitId");
  const weakLanes = rankedWeakLanes(result);
  const strongLanes = rankedStrongLanes(result);
  const strategy = result.strategyFaced;
  const detections = detectBattlePatterns(result, weakLanes);
  const knownPatterns = updateTacticalPatterns(normalizedMemory.knownPatterns, detections, result);
  const patternConfidence = Object.fromEntries(knownPatterns.map((pattern) => [pattern.key, pattern.confidence])) as PlayerMemory["patternConfidence"];
  const preferredOpeningCounters = knownPatterns.filter((pattern) => pattern.confidence >= 0.7 || pattern.seenCount >= 2).slice(0, 5).map(counterForPattern);
  const veterans = mergeVeterans(normalizedMemory.veterans, result);
  const rivalry = updateRivalry(normalizedMemory, result, detections, wins, losses);
  const rewards = computeBattleRewards(normalizedMemory, result, detections, rivalry, veterans);
  const wonAgainstStrategies = strategy && result.result === "win" ? mergeRecent(normalizedMemory.wonAgainstStrategies, [strategy], 8) : normalizedMemory.wonAgainstStrategies;
  const bestEndlessTime = result.mode === "endless" ? Math.max(normalizedMemory.bestEndlessTime ?? 0, result.battleTime) : normalizedMemory.bestEndlessTime ?? 0;
  // Daily Seeded Challenge: score this run and fold it into the persistent record (best + streak).
  const dailyChallenge =
    result.mode === "daily" && typeof result.dailySeed === "number"
      ? nextDailyRecord(normalizedMemory.dailyChallenge, {
          dateKey: dateKeyOf(new Date()),
          score: dailyScore({ win: result.result === "win", battleTime: result.battleTime, weight: dailyChallengeFromSeed(result.dailySeed).weight }),
          win: result.result === "win"
        })
      : normalizedMemory.dailyChallenge;
  const achievementResult = evaluateAchievements(normalizedMemory.achievements, {
    result,
    wins,
    battleCount,
    rivalryStage: rivalry.rivalryStage,
    commanderLevel: rewards.commanderLevel,
    veterans,
    wonAgainstStrategies,
    rewardReasons: rewards.summary.reasons,
    dailyStreak: dailyChallenge?.streak ?? 0,
    dailyCompleted: Boolean(dailyChallenge?.completed),
    bestEndlessTime,
    ownedSkinsCount: (normalizedMemory.ownedSkins ?? []).length,
    lifetimeWarFunds: rewards.lifetimeWarFunds
  });
  const nickname = nicknameCandidate(knownPatterns, result);
  const learning = classifyBattleLearning(result, detections, weakLanes);
  const recentLearnings = [learning, ...(normalizedMemory.recentLearnings ?? [])].slice(0, 8);
  const nextMemoryForSummary: PlayerMemory = {
    ...normalizedMemory,
    ...rivalry,
    recentLearnings,
    nickname: nickname?.nickname ?? normalizedMemory.nickname,
    nicknameReason: nickname?.reason ?? normalizedMemory.nicknameReason,
    nicknameAssignedAt: nickname ? new Date().toISOString() : normalizedMemory.nicknameAssignedAt
  };

  return {
    ...normalizedMemory,
    battleCount,
    wins,
    losses,
    averageSurvivalTime,
    favoriteTowers: mergeRecent(normalizedMemory.favoriteTowers, favoriteTowers, 8),
    favoriteUnits: mergeRecent(normalizedMemory.favoriteUnits, favoriteUnits, 8),
    weakLanes: mergeRecent(normalizedMemory.weakLanes, weakLanes, 5),
    strongLanes: mergeRecent(normalizedMemory.strongLanes, strongLanes, 5),
    lostToStrategies: strategy && result.result === "loss" ? mergeRecent(normalizedMemory.lostToStrategies, [strategy], 8) : normalizedMemory.lostToStrategies,
    wonAgainstStrategies,
    knownPatterns,
    patternConfidence,
    preferredOpeningCounters,
    veterans,
    rivalryScore: rivalry.rivalryScore,
    rivalryStage: rivalry.rivalryStage,
    notableHistory: rivalry.notableHistory,
    recentLearnings,
    nickname: nickname?.nickname ?? normalizedMemory.nickname,
    nicknameReason: nickname?.reason ?? normalizedMemory.nicknameReason,
    nicknameAssignedAt: nickname ? new Date().toISOString() : normalizedMemory.nicknameAssignedAt,
    commonPatterns: mergeRecent(normalizedMemory.commonPatterns, detections.map((detection) => detection.key), 12),
    commanderLevel: rewards.commanderLevel,
    commanderXp: rewards.commanderXp,
    warFunds: rewards.warFunds,
    lifetimeWarFunds: rewards.lifetimeWarFunds,
    bestEndlessTime,
    lastRewardSummary: { ...rewards.summary, unlockedAchievements: achievementResult.unlocked },
    achievements: achievementResult.achievements,
    dailyChallenge,
    summaryForPrompt: memorySummary(result, report, weakLanes, strongLanes, knownPatterns, veterans, nextMemoryForSummary),
    commanderGrudgeLevel: Math.min(10, normalizedMemory.commanderGrudgeLevel + 1),
    updatedAt: new Date().toISOString()
  };
}
